import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import { useT, useLang } from '../../lib/i18n.jsx';
import { get } from '../../lib/api.js';
import { useAsync, initials } from '../../lib/hooks.js';
import { toast } from '../../lib/toast.jsx';
import { errorMessage } from '../../lib/errors.js';
import { relativeTime } from '../../lib/format.js';
import { hostingState } from '../../lib/status.js';
import { useBackupSummaries } from '../../lib/backups.js';
import NewPlanModal from './modals/NewPlanModal.jsx';

export default function HostingList() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const { data: hostings, loading, error, reload } = useAsync(() => get('/hostings'), []);
  const backups = useBackupSummaries(hostings);
  const [newOpen, setNewOpen] = useState(params.get('action') === 'newPlan');

  const plans = (hostings || []).map((h) => toPlan(h, backups, lang));
  const totalStorageMax = plans.reduce((a, p) => a + p.storage.total, 0);
  // /servers is admin-only; the hostings carry their own server, which is all
  // a customer needs to see.
  const servers = useMemo(() => {
    const map = new Map();
    for (const h of hostings || []) if (h.server) map.set(h.server.id, h.server);
    return Array.from(map.values());
  }, [hostings]);
  const siteCount = (n) => t(n === 1 ? 'hostingList.sites.one' : 'hostingList.sites.other', { n });

  return (
    <main className="page" data-screen-label="Hosting">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('host.title')}</h1>
          <p className="page-sub">{t('host.sub')}</p>
        </div>
        <div className="actions">
          <button className="btn-primary" onClick={() => setNewOpen(true)}>
            <Icon name="plus" size={15} stroke={2} />
            <span style={{ marginLeft: 6 }}>{t('host.new')}</span>
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <KpiCard
          label={t('host.kpi.sites')}
          value={plans.length}
          sub={
            servers.length === 0
              ? '—'
              : t(servers.length === 1 ? 'hostingList.onServers.one' : 'hostingList.onServers.other', { n: servers.length })
          }
          icon="server"
        />
        <KpiCard
          label={t('host.kpi.storage')}
          value={`${totalStorageMax} GB`}
          sub={t('hostingList.storage.allocated')}
          icon="archive"
        />
        <KpiCard
          label={t('host.kpi.traffic')}
          value="—"
          sub={t('hostingList.traffic.none')}
          icon="upload"
        />
      </div>

      <div className="dash-grid">
        <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading && <div className="state-card">{t('common.loading')}</div>}
          {!loading && error && (
            <div className="state-card" role="alert">
              <h3>{t('hostingList.error')}</h3>
              <p>{errorMessage(error, { lang })}</p>
            </div>
          )}
          {!loading && !error && plans.length === 0 && (
            <div className="state-card">
              <h3>{t('hostingList.empty')}</h3>
              <p>{t('hostingList.empty.body')}</p>
            </div>
          )}
          {plans.map((p) => (
            <HostingPlanCard key={p.id} plan={p} lang={lang} t={t} onManage={() => navigate(`/hosting/${p.id}`)} />
          ))}
        </section>

        <aside className="side-stack">
          <section className="card">
            <div className="card-h"><h3>{t('host.server.title')}</h3></div>
            <div className="server-list">
              {servers.map((s) => (
                <div className="server-row" key={s.id}>
                  <div className="server-row__head">
                    <span className="server-row__name mono">{s.host}</span>
                    <span className={'status-pill ' + (s.status === 'online' ? '' : s.status === 'error' || s.status === 'offline' ? 'is-danger' : 'is-warn')}>
                      <span className="dot" />
                      {SERVER_STATUSES.includes(s.status) ? t(`hostingList.serverStatus.${s.status}`) : s.status}
                    </span>
                  </div>
                  <div className="server-row__meta">
                    <span>{s.name}</span>
                    <span>·</span>
                    <span>{siteCount(plans.filter((p) => p.serverId === s.id).length)}</span>
                  </div>
                </div>
              ))}
              {servers.length === 0 && (
                <div style={{ padding: '12px 22px', color: 'var(--ink-3)', fontSize: 13 }}>
                  {t('hostingList.servers.none')}
                </div>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-h"><h3>{t('host.tools.title')}</h3></div>
            <div className="tool-list">
              <ToolRow icon="archive" label={t('host.tools.files')} sub={t('host.tools.files.d')} onClick={() => plans[0] ? navigate(`/hosting/${plans[0].id}`) : toast(t('hostingList.addPlanFirst'))} />
              <ToolRow icon="server" label={t('host.tools.db')} sub={t('host.tools.db.d')} onClick={() => plans[0] ? navigate(`/hosting/${plans[0].id}`) : toast(t('hostingList.addPlanFirst'))} />
              <ToolRow icon="shield" label={t('host.tools.ssh')} sub={t('host.tools.ssh.d')} onClick={() => plans[0] ? navigate(`/hosting/${plans[0].id}`) : toast(t('hostingList.addPlanFirst'))} />
              <ToolRow icon="rotate" label={t('host.tools.cron')} sub={t('host.tools.cron.d')} onClick={() => plans[0] ? navigate(`/hosting/${plans[0].id}`) : toast(t('hostingList.addPlanFirst'))} />
            </div>
          </section>
        </aside>
      </div>

      {newOpen && (
        <NewPlanModal
          lang={lang}
          onClose={() => {
            setNewOpen(false);
            if (params.get('action')) {
              params.delete('action');
              setParams(params, { replace: true });
            }
          }}
          onCreated={() => {
            setNewOpen(false);
            reload();
          }}
        />
      )}
    </main>
  );
}

const SERVER_STATUSES = ['online', 'offline', 'provisioning', 'error'];

function toPlan(h, backups, lang) {
  const diskGb = parseFloat(String(h.package?.disk_limit || '0').replace(/[^\d.]/g, '')) || 0;
  const last = backups?.[h.id]?.lastCompletedAt;
  return {
    id: h.id,
    domain: h.domain,
    fav: initials(h.domain.split('.')[0]),
    plan: h.package?.name || '—',
    server: h.server?.host || '—',
    serverId: h.server_id,
    php: h.runtime || '—',
    ssl: h.ssl_enabled ? (h.ssl_provider === 'letsencrypt' || !h.ssl_provider ? "Let's Encrypt" : h.ssl_provider) : '—',
    mailboxes: h.modules?.email?.count ?? null,
    lastBackup: backups == null ? '…' : last ? relativeTime(last, lang) : '—',
    storage: { total: diskGb },
    state: hostingState(h, lang),
  };
}

function KpiCard({ label, value, sub, icon, progress }) {
  return (
    <div className="kpi-card">
      <div className="kpi-card__top">
        <span className="kpi-card__icon"><Icon name={icon} size={16} /></span>
        <span className="kpi-card__label">{label}</span>
      </div>
      <div className="kpi-card__value">{value}</div>
      <div className="kpi-card__sub">{sub}</div>
      {progress !== undefined && (
        <div className="bar kpi-card__bar">
          <i style={{ width: Math.round(progress * 100) + '%' }} />
        </div>
      )}
    </div>
  );
}

function HostingPlanCard({ plan, lang, t, onManage }) {
  return (
    <section className="card plan-card">
      <div className="plan-head">
        <div className="plan-head__id">
          <div className="fav">{plan.fav}</div>
          <div>
            <div className="plan-head__domain">{plan.domain}</div>
            <div className="plan-head__plan">
              {plan.plan} · <span className="mono">{plan.server}</span>
            </div>
          </div>
        </div>
        <div className="plan-head__right">
          <span className={`status-pill ${plan.state.tone}`.trim()} title={plan.state.message || undefined}>
            <span className="dot" />
            {plan.state.label}
          </span>
          <button className="btn-ghost" onClick={onManage}>{t('host.manage')} →</button>
        </div>
      </div>

      <div className="plan-specs">
        <Spec label={t('host.spec.php')} value={plan.php} />
        <Spec label={t('host.spec.ssl')} value={plan.ssl} />
        <Spec label={t('host.spec.mailboxes')} value={plan.mailboxes == null ? '—' : String(plan.mailboxes)} />
        <Spec label={t('host.spec.lastbackup')} value={plan.lastBackup} />
      </div>
    </section>
  );
}

function Spec({ label, value, hint }) {
  return (
    <div className="spec">
      <div className="spec__label">{label}</div>
      <div className="spec__value">{value}</div>
      {hint && <div className="spec__hint">{hint}</div>}
    </div>
  );
}

function ToolRow({ icon, label, sub, onClick }) {
  return (
    <button className="tool-row" type="button" onClick={onClick}>
      <span className="tool-row__icon"><Icon name={icon} size={16} /></span>
      <span className="tool-row__body">
        <span className="tool-row__label">{label}</span>
        <span className="tool-row__sub">{sub}</span>
      </span>
      <span className="tool-row__arrow"><Icon name="arrow-right" size={14} /></span>
    </button>
  );
}
